'use client'

import { useState, useSyncExternalStore } from 'react'
import Link from 'next/link'
import enMessages from '@/messages/en-US.json'
import itMessages from '@/messages/it.json'
import HoverGradientButton from '@components/buttons/HoverGradientButton/HoverGradientButton'
import ConsentSettings from '@components/settings/ConsentSettings/ConsentSettings'
import { readConsent, writeConsent, isGpcEnabled, CONSENT_CHANGE_EVENT } from '@lib/consent/client'
import './consentBanner.css'

type ConsentMessages = typeof enMessages.consent

const messagesByLocale: Record<string, ConsentMessages> = {
  'en-US': enMessages.consent,
  it: itMessages.consent,
}

function subscribeToConsent(onChange: () => void) {
  window.addEventListener(CONSENT_CHANGE_EVENT, onChange)
  return () => window.removeEventListener(CONSENT_CHANGE_EVENT, onChange)
}

const subscribeNever = () => () => {}

/**
 * First-run cookie consent banner. The root layout is statically generated,
 * so the decision happens client-side: the server snapshot reports consent
 * as already given (banner hidden) and the client corrects it after
 * hydration. Renders nothing once a valid, current-version choice exists.
 */
export default function ConsentBanner({ locale }: { locale: string }) {
  const hasConsent = useSyncExternalStore(
    subscribeToConsent,
    () => readConsent() !== null,
    () => true,
  )
  const gpc = useSyncExternalStore(subscribeNever, isGpcEnabled, () => false)
  const [customizing, setCustomizing] = useState(false)

  // Stay open while the customize panel is in use, even though toggling
  // in it already persists a choice.
  if (hasConsent && !customizing) return null

  const m = messagesByLocale[locale] ?? enMessages.consent

  return (
    <div
      id="consent-banner"
      role="dialog"
      aria-modal="false"
      aria-label={m.banner.title}
      className="consent-banner"
    >
      <div className="consent-banner-inner">
        <p className="consent-banner-title">{m.banner.title}</p>
        <p className="consent-banner-body">
          {m.banner.body}{' '}
          <Link href={`/${locale}/cookie-policy`} className="underline">
            {m.banner.policyLink}
          </Link>
        </p>
        {gpc && <p className="consent-banner-gpc-notice">{m.settings.gpcNotice}</p>}
        {customizing && (
          <div className="consent-banner-settings">
            <ConsentSettings labels={m.settings} />
          </div>
        )}
        <div className="consent-banner-actions">
          {customizing ? (
            <HoverGradientButton
              id="consent-banner-close"
              variant="filled"
              className="consent-banner-button"
              onClick={() => {
                writeConsent(readConsent()?.analytics === true)
                setCustomizing(false)
              }}
            >
              {m.banner.done}
            </HoverGradientButton>
          ) : (
            <>
              <HoverGradientButton
                id="consent-banner-accept"
                variant="filled"
                className="consent-banner-button"
                onClick={() => writeConsent(true)}
              >
                {m.banner.acceptAll}
              </HoverGradientButton>
              <HoverGradientButton
                id="consent-banner-reject"
                variant="outline"
                className="consent-banner-button"
                onClick={() => writeConsent(false)}
              >
                {m.banner.essentialOnly}
              </HoverGradientButton>
              <button
                type="button"
                id="consent-banner-customize"
                onClick={() => setCustomizing(true)}
                className="consent-banner-customize-link"
              >
                {m.banner.customize}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
